import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ["window", "header", "handle"];

    connect() {
        setTimeout(() => {
            this._setToHighestZ();
        }, 0);
    }

    closeWindow(e) {
        console.log("Close button clicked!");
        const winEl = e.currentTarget.closest('[data-williams-window-target="window"]');
        if (winEl) {
            winEl.remove();
        }
    }

    startDrag(e) {
        // Ignore drag if close button is clicked
        if (e.target.closest('.w-close')) {
            return;
        }

        console.log("Drag started!");
        if (e.button !== 0 && e.pointerType === "mouse") return;

        const headerEl = e.currentTarget;
        const winEl = headerEl.closest('[data-williams-window-target="window"]');
        if (!winEl) return;

        const cs = getComputedStyle(winEl);
        const startTop = parseFloat(cs.top) || 0;
        const startLeft = parseFloat(cs.left) || 0;

        this._drag = {
            pointerId: e.pointerId,
            winEl,
            startX: e.clientX,
            startY: e.clientY,
            startTop,
            startLeft
        };

        headerEl.setPointerCapture?.(e.pointerId);
        window.addEventListener("pointermove", this._onDragMove, { passive: false });
        window.addEventListener("pointerup", this._onDragEnd, { once: true });
        window.addEventListener("pointercancel", this._onDragEnd, { once: true });

        this._setToHighestZ();
    }

    startResize = (e) => {
        if (e.button !== 0 && e.pointerType === "mouse") return;

        const handleEl = e.currentTarget;
        const winEl = handleEl.closest('[data-williams-window-target="window"]');
        if (!winEl) return;

        const cs = getComputedStyle(winEl);
        const startWidth = parseFloat(cs.width);
        const startHeight = parseFloat(cs.height);
        const startLeft = parseFloat(cs.left) || 0;
        const startTop = parseFloat(cs.top) || 0;

        this._resize = {
            pointerId: e.pointerId,
            winEl,
            startX: e.clientX,
            startY: e.clientY,
            startWidth,
            startHeight,
            startLeft,
            startTop,
            // Minimums (tweak as you like)
            minW: 220,
            minH: Math.max(120, parseFloat(getComputedStyle(this.headerTarget).height) + 60)
        };

        handleEl.setPointerCapture?.(e.pointerId);
        window.addEventListener("pointermove", this._onResizeMove, { passive: false });
        window.addEventListener("pointerup", this._onResizeEnd, { once: true });
        window.addEventListener("pointercancel", this._onResizeEnd, { once: true });

        this._setToHighestZ();
    };

    _setToHighestZ() {
        const highestZ = this._highestZ();
        this.windowTarget.style.zIndex = highestZ + 1;
    }

    _highestZ() {
        const windows = document.querySelectorAll('[data-williams-window-target="window"]');
        let highest = 1;
        windows.forEach((win) => {
            const z = parseInt(getComputedStyle(win).zIndex) || 0;
            if (z > highest) highest = z;
        });
        return highest;
    }

    _containing_element() {
        // data-williams-window-initial-top-value
        const containerSelector = this.data.get("containerSelector");
        return document.querySelector(containerSelector);
    }

    _onDragMove = (e) => {
        const d = this._drag;
        if (!d) return;
        e.preventDefault();

        const deltaX = e.clientX - d.startX;
        const deltaY = e.clientY - d.startY;

        const cont = this._containing_element();
        const win = d.winEl;

        let nextLeft = d.startLeft + deltaX;
        let nextTop = d.startTop + deltaY;

        const maxLeft = cont.clientWidth - win.offsetWidth;
        const maxTop = cont.clientHeight - win.offsetHeight;

        win.style.left = `${Math.max(0, Math.min(maxLeft, nextLeft))}px`;
        win.style.top = `${Math.max(0, Math.min(maxTop, nextTop))}px`;
    };

    _onDragEnd = () => {
        window.removeEventListener("pointermove", this._onDragMove);
        this._drag = null;
    };

    _onResizeMove = (e) => {
        const r = this._resize;
        if (!r) return;
        e.preventDefault();

        const cont = this._containing_element();    // .w-cont (position: relative)
        const win = r.winEl;

        // proposed new size from bottom-right drag
        let nextW = r.startWidth + (e.clientX - r.startX);
        let nextH = r.startHeight + (e.clientY - r.startY);

        // clamp to minimums
        nextW = Math.max(r.minW, nextW);
        nextH = Math.max(r.minH, nextH);

        // clamp so the window stays fully inside the container
        const maxW = cont.clientWidth - r.startLeft;
        const maxH = cont.clientHeight - r.startTop;
        nextW = Math.min(nextW, maxW);
        nextH = Math.min(nextH, maxH);

        win.style.width = `${nextW}px`;
        win.style.height = `${nextH}px`;
    };

    _onResizeEnd = () => {
        window.removeEventListener("pointermove", this._onResizeMove);
        this._resize = null;
    };
}
